(() => {
  const dialogSelector = '#shopify-pc__prefs__dialog';
  const config = window.SBCustomerPrivacyDialogConfig || {};
  const checkboxOffSvg = `${config.checkboxOffSvg || ''}`;
  const checkboxOnSvg = `${config.checkboxOnSvg || ''}`;
  let pageBackdrop = document.querySelector('body > .sb-modal-page-backdrop--privacy');
  let privacyWasVisible = null;

  const syncPageBackdrop = () => {
    const banner = document.querySelector('#shopify-pc__banner');
    const preferences = document.querySelector('#shopify-pc__prefs');
    const surfaceIsVisible = (surface) => (
      surface instanceof HTMLElement
      && !surface.hidden
      && surface.style.display !== 'none'
      && window.getComputedStyle(surface).display !== 'none'
    );
    const bannerIsVisible = surfaceIsVisible(banner);
    const preferencesAreVisible = surfaceIsVisible(preferences);
    const isVisible = bannerIsVisible || preferencesAreVisible;
    const visibilityChanged = privacyWasVisible !== isVisible;
    privacyWasVisible = isVisible;
    // Expose the native privacy surface state to Safari's top-edge color sampler.
    document.documentElement.classList.toggle('sb-customer-privacy-open', isVisible);

    if (isVisible && !pageBackdrop?.isConnected) {
      pageBackdrop = document.createElement('div');
      pageBackdrop.className = 'sb-modal-page-backdrop sb-modal-page-backdrop--privacy';
      pageBackdrop.setAttribute('aria-hidden', 'true');
      document.body.prepend(pageBackdrop);
    }
    if (pageBackdrop) {
      pageBackdrop.classList.toggle(
        'sb-modal-page-backdrop--privacy-banner',
        bannerIsVisible && !preferencesAreVisible
      );
    }
    if (!isVisible && pageBackdrop) {
      pageBackdrop.remove();
      pageBackdrop = null;
    }
    if (visibilityChanged) {
      document.dispatchEvent(new CustomEvent('sb:customer-privacy:state-change', {
        detail: { open: isVisible },
      }));
    }
  };

  const surfaceSelector = '#shopify-pc__banner, #shopify-pc__prefs';
  const surfaceObserver = new MutationObserver(syncPageBackdrop);
  const observeSurfaces = () => {
    surfaceObserver.disconnect();
    document.querySelectorAll(surfaceSelector).forEach((surface) => {
      surfaceObserver.observe(surface, { attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
    });
    syncPageBackdrop();
  };
  observeSurfaces();

  // Shopify inserts and replaces its surfaces asynchronously. Only their own
  // visibility attributes matter; page animations must not trigger layout reads.
  const pageBackdropObserver = new MutationObserver((mutations) => {
    const surfacesChanged = mutations.some((mutation) =>
      [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
        node instanceof Element && (node.matches(surfaceSelector) || node.querySelector(surfaceSelector))
      )
    );
    if (surfacesChanged) observeSurfaces();
  });
  pageBackdropObserver.observe(document.body, { childList: true, subtree: true });

  if (!checkboxOffSvg || !checkboxOnSvg) return;

  let checkboxBackgrounds = null;
  let checkboxOffFillColor = '';
  let checkboxOnFillColor = '';
  const toInlineSvgDataUrl = (svgContent) => `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}")`;

  const getCheckboxBackgrounds = () => {
    const computedStyles = window.getComputedStyle(document.documentElement);
    const nextOffFillColor = computedStyles
      .getPropertyValue('--sb-color-neutral-900')
      .trim();
    const nextOnFillColor = computedStyles
      .getPropertyValue('--sb-color-primary-500')
      .trim();
    if (!nextOffFillColor || !nextOnFillColor) return checkboxBackgrounds;
    if (
      checkboxBackgrounds
      && checkboxOffFillColor === nextOffFillColor
      && checkboxOnFillColor === nextOnFillColor
    ) {
      return checkboxBackgrounds;
    }
    checkboxOffFillColor = nextOffFillColor;
    checkboxOnFillColor = nextOnFillColor;
    checkboxBackgrounds = {
      off: toInlineSvgDataUrl(checkboxOffSvg.replaceAll('fill="white"', `fill="${nextOffFillColor}"`)),
      on: toInlineSvgDataUrl(checkboxOnSvg.replaceAll('fill="white"', `fill="${nextOnFillColor}"`)),
    };
    return checkboxBackgrounds;
  };

  const applyCheckboxVisualState = (checkboxNode) => {
    if (!(checkboxNode instanceof HTMLInputElement)) return;
    const iconNode = checkboxNode.closest('label')?.querySelector('span[aria-hidden="true"]');
    if (!(iconNode instanceof HTMLElement)) return;
    const backgrounds = getCheckboxBackgrounds();
    if (!backgrounds) return;
    const { off, on } = backgrounds;
    if (checkboxNode.checked) {
      iconNode.style.backgroundImage = `${on}, linear-gradient(var(--sb-color-always-white), var(--sb-color-always-white))`;
      iconNode.style.backgroundSize = '20px 20px, 16px 16px';
      iconNode.style.backgroundPosition = 'center center, center center';
      iconNode.style.backgroundRepeat = 'no-repeat, no-repeat';
    } else {
      iconNode.style.backgroundImage = off;
      iconNode.style.backgroundSize = '20px 20px';
      iconNode.style.backgroundPosition = 'center center';
      iconNode.style.backgroundRepeat = 'no-repeat';
    }
    iconNode.style.backgroundColor = 'transparent';
  };

  const syncCheckboxes = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;
    scope.querySelectorAll(`${dialogSelector} .shopify-pc__prefs__option input[type="checkbox"]`).forEach((checkboxNode) => {
      if (!(checkboxNode instanceof HTMLInputElement)) return;
      applyCheckboxVisualState(checkboxNode);
      if (checkboxNode.dataset.sbCustomerPrivacyCheckboxBound === 'true') return;
      const syncCheckboxVisualState = () => applyCheckboxVisualState(checkboxNode);
      checkboxNode.addEventListener('change', syncCheckboxVisualState);
      checkboxNode.addEventListener('input', syncCheckboxVisualState);
      checkboxNode.dataset.sbCustomerPrivacyCheckboxBound = 'true';
    });
  };

  const observedDialogs = new WeakSet();

  const observeDialog = (dialog) => {
    if (!(dialog instanceof HTMLElement) || observedDialogs.has(dialog)) return;
    observedDialogs.add(dialog);

    syncCheckboxes(document);

    let isQueued = false;
    const dialogObserver = new MutationObserver(() => {
      if (isQueued) return;
      isQueued = true;
      requestAnimationFrame(() => {
        isQueued = false;
        syncCheckboxes(document);
      });
    });
    dialogObserver.observe(dialog, { childList: true, subtree: true });
  };

  const scanForDialog = () => {
    const dialog = document.querySelector(dialogSelector);
    if (dialog) observeDialog(dialog);
  };

  scanForDialog();

  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(dialogSelector) || node.querySelector(dialogSelector)) {
          scanForDialog();
          return;
        }
      }
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  const themeObserver = new MutationObserver(() => {
    checkboxBackgrounds = null;
    syncCheckboxes(document);
  });
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
})();
